import {
  format as formatDateFns,
  formatDistanceToNowStrict,
  isThisYear,
  isToday,
  isYesterday,
  type Locale,
} from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatFileSize } from '../lib/files';

import { DEFAULT_LANGUAGE, LANGUAGES, toSupportedLanguage, type Language } from './languages';

export type DateInput = Date | string | number;

export const toDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value));

export type DateStyle =
  'date' | 'dateShort' | 'dateLong' | 'time' | 'dateTime' | 'dateTimeShort' | 'monthYear' | 'weekday';

const PATTERNS: Record<DateStyle, string> = {
  date: 'P', // 09.10.2026 / 09/10/2026
  dateShort: 'd MMM', // 9 окт.
  dateLong: 'PPP', // 9 октября 2026 г.
  time: 'p', // 14:05
  dateTime: 'P p',
  dateTimeShort: 'd MMM, p',
  monthYear: 'LLLL yyyy',
  weekday: 'EEEE',
};

export interface Formatters {
  language: Language;
  /** BCP-47 tag for Intl APIs. */
  intlLocale: string;
  dateLocale: Locale;
  date: (value: DateInput, style?: DateStyle) => string;
  /** date-fns pattern with the active locale. */
  pattern: (value: DateInput, pattern: string) => string;
  /** "5 минут назад" / "in 3 days". */
  relative: (value: DateInput) => string;
  /** Compact timestamp for lists: time for today, "вчера, 14:05", "9 окт.", "9 окт. 2025". */
  smart: (value: DateInput, labels: { yesterday: string }) => string;
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  fileSize: (bytes: number) => string;
  list: (items: string[], type?: Intl.ListFormatType) => string;
}

export function createFormatters(languageInput: string | undefined): Formatters {
  const language = toSupportedLanguage(languageInput) ?? DEFAULT_LANGUAGE;
  const meta = LANGUAGES[language];
  const numberFormat = new Intl.NumberFormat(meta.intl);
  const options = { locale: meta.dateFns };

  return {
    language,
    intlLocale: meta.intl,
    dateLocale: meta.dateFns,
    date: (value, style = 'date') => formatDateFns(toDate(value), PATTERNS[style], options),
    pattern: (value, pattern) => formatDateFns(toDate(value), pattern, options),
    relative: (value) => formatDistanceToNowStrict(toDate(value), { ...options, addSuffix: true }),
    smart: (value, labels) => {
      const date = toDate(value);
      if (isToday(date)) return formatDateFns(date, 'p', options);
      if (isYesterday(date)) return `${labels.yesterday}, ${formatDateFns(date, 'p', options)}`;
      return formatDateFns(date, isThisYear(date) ? 'd MMM' : 'd MMM yyyy', options);
    },
    number: (value, numberOptions) =>
      numberOptions
        ? new Intl.NumberFormat(meta.intl, numberOptions).format(value)
        : numberFormat.format(value),
    fileSize: (bytes) => formatFileSize(bytes, meta.intl),
    list: (items, type = 'conjunction') => new Intl.ListFormat(meta.intl, { type }).format(items),
  };
}

/** Locale-aware formatters bound to the active UI language; re-render on language change. */
export function useFormatters(): Formatters {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  return useMemo(() => createFormatters(language), [language]);
}

/** The date-fns locale for the active language (for calendars and custom formatting). */
export const useDateLocale = (): Locale => useFormatters().dateLocale;
